import { useState, useMemo, useCallback, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import { TopBar } from "./TopBar";
import { MobileNav } from "./MobileNav";
import { ActionPageContext } from "./actionPage";
import { apiIdle } from "@/lib/api";
import { clearReturn } from "@/lib/drafts";
import { SessionTimer } from "./SessionTimer";
import { useI18n } from "@/i18n/I18nContext";

// After each page change: page title from the visible h1, focus on that heading, scroll to top.
function usePageFocus(active) {
  const { pathname } = useLocation();
  useEffect(() => {
    let tries = 0;
    let timer;
    const run = () => {
      const h = [...document.querySelectorAll("#main-content h1")].find((el) => el.offsetParent !== null);
      if (h) {
        document.title = `${h.textContent.trim()} · VISIBILIS Control OS`;
        h.setAttribute("tabindex", "-1");
        h.focus({ preventScroll: true });
        window.scrollTo(0, 0);
        return;
      }
      if (tries++ < 25) timer = setTimeout(run, 150);
    };
    timer = setTimeout(run, 60);
    return () => clearTimeout(timer);
  }, [pathname, active]);
}

// A deep link to an action the current state does not offer: once the page has loaded (heading visible, no request pending
// for ~450 ms) without claiming it, say so.
function useUnclaimedAction(active, marked) {
  const { pathname } = useLocation();
  const aktion = marked?.path === pathname ? marked.aktion : null;
  const [shown, setShown] = useState(null);
  useEffect(() => {
    setShown(null);
    if (!aktion || active > 0) return undefined;
    let tries = 0;
    let stable = 0;
    let timer;
    const check = () => {
      const ready = apiIdle() && [...document.querySelectorAll("#main-content h1")].some((el) => el.offsetParent !== null);
      stable = ready ? stable + 1 : 0;
      if (stable >= 3) { setShown(pathname); return; }
      if (tries++ < 200) timer = setTimeout(check, 150);
    };
    timer = setTimeout(check, 150);
    return () => clearTimeout(timer);
  }, [pathname, active, aktion]);
  return shown === pathname && active === 0 ? pathname.replace(/\/(aktion\/)?[^/]+$/, "") : null;
}

function UnavailableNotice({ back }) {
  return (
    <div role="status" className="mb-4 rounded-xl border border-[#E3DDF0] bg-[#EEEAF7] px-4 py-3 text-sm text-brand-purple" data-testid="action-unavailable-notice">
      Diese Aktion ist im aktuellen Zustand nicht verfügbar oder für deine Rolle nicht freigegeben. Angezeigt wird die Detailansicht.{" "}
      <Link to={back} replace className="font-semibold underline" data-testid="action-unavailable-back">Zur Detailadresse wechseln</Link>
    </div>
  );
}

export function ControlLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [host, setHost] = useState(null);
  const [active, setActive] = useState(0);
  const enter = useCallback(() => setActive((a) => a + 1), []);
  const leave = useCallback(() => setActive((a) => a - 1), []);
  const [marked, setMarked] = useState(null);
  const mark = useCallback((aktion) => setMarked({ path: window.location.pathname, aktion }), []);
  const ctx = useMemo(() => ({ host, enter, leave, mark }), [host, enter, leave, mark]);
  useEffect(() => {
    clearReturn();
  }, []);
  const unavailableBack = useUnclaimedAction(active, marked);
  const { t } = useI18n();
  usePageFocus(active);

  return (
    <ActionPageContext.Provider value={ctx}>
      <SessionTimer />
      <div className="min-h-screen bg-[#F6F5FB]">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-brand-purple focus:px-4 focus:py-2 focus:text-white"
        >
          {t("common.skip_to_content")}
        </a>
        <Sidebar />
        <div className="min-w-0 lg:pl-60">
          <TopBar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
          <main id="main-content" tabIndex={-1} className="mx-auto min-w-0 max-w-7xl px-4 pb-28 pt-6 focus:outline-none sm:px-6 lg:px-8 lg:py-8">
            {unavailableBack && <UnavailableNotice back={unavailableBack} />}
            <div hidden={active > 0}>{children}</div>
            <div ref={setHost} data-testid="action-page-host" />
          </main>
        </div>
        <MobileNav onMore={() => setMobileOpen(true)} moreOpen={mobileOpen} />
      </div>
    </ActionPageContext.Provider>
  );
}
